import { useCallback, useEffect, useRef } from 'react';
import { useKey } from 'react-use';

import { SamplerInstance, getSampler } from './globalSamplers';
import { OnKeyPlayedFn } from './types';

export type KeySoundValues = {
  playNote: () => void;
  stopNote: () => void;
};

function useThrottleCallback(callback: () => void, delay: number): () => void {
  /**
   * Creates a throttled version of a callback function.
   *
   * This hook returns a function that, when invoked, will only execute the provided
   * callback if at least `delay` milliseconds have passed since its last execution.
   * This is used so that we don't allow a user to somehow accidentally play a large
   * number of notes at once
   */
  const lastCallRef = useRef<number>(0);
  return useCallback(() => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      callback();
    }
  }, [callback, delay]);
}

export function useKeySound(
  letter: string,
  note: string,
  drawRipple: () => void,
  onKeyPlayed?: OnKeyPlayedFn,
): KeySoundValues {
  const isKeyPressed = useRef<boolean>(false);
  const samplerRef = useRef<SamplerInstance | null>(null);

  useEffect(
    function loadSamplerForNote() {
      getSampler(note).then((sampler: SamplerInstance) => {
        samplerRef.current = sampler;
      });
      return () => {};
    },
    [note],
  );

  const playNote = useThrottleCallback(() => {
    if (!isKeyPressed.current) {
      drawRipple();
      samplerRef.current?.triggerAttack(note);
      onKeyPlayed?.(letter);

      isKeyPressed.current = true;
    }
  }, 100);

  const stopNote = useCallback(() => {
    samplerRef.current?.triggerRelease(note);
    isKeyPressed.current = false;
  }, [note]);

  const keyFilter = useCallback(
    (event: KeyboardEvent) => {
      return event.key.toUpperCase() === letter.toUpperCase();
    },
    [letter],
  );

  useKey(keyFilter, stopNote, { event: 'keyup' }, [keyFilter, stopNote]);
  useKey(keyFilter, playNote, { event: 'keydown' }, [keyFilter, playNote]);

  return {
    playNote,
    stopNote,
  };
}
